import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User, UserGift } from '@/lib/api';

interface UserStore {
  currentUser: User | null;
  userGifts: UserGift[];
  isLoadingGifts: boolean;
  giftsError: string | null;
  
  // User actions
  setCurrentUser: (user: User | null) => void;
  clearUser: () => void;
  
  // Gifts actions
  setUserGifts: (gifts: UserGift[]) => void;
  setLoadingGifts: (loading: boolean) => void;
  setGiftsError: (error: string | null) => void;
  clearGifts: () => void;
  
  // Combined actions
  clearAll: () => void;
}

export const useUserStore = create<UserStore>()(
  persist(
    (set, get) => ({
      currentUser: null,
      userGifts: [],
      isLoadingGifts: false,
      giftsError: null,
      
      // User actions
      setCurrentUser: (user) => set({ currentUser: user }),
      clearUser: () => set({ currentUser: null }),
      
      // Gifts actions
      setUserGifts: (gifts) => set({ userGifts: gifts, giftsError: null }),
      setLoadingGifts: (loading) => set({ isLoadingGifts: loading }),
      setGiftsError: (error) => set({ giftsError: error }),
      clearGifts: () => set({ userGifts: [], giftsError: null }),
      
      // Combined actions
      clearAll: () => set({ 
        currentUser: null, 
        userGifts: [], 
        isLoadingGifts: false,
        giftsError: null 
      }),
    }),
    {
      name: 'user-store', // persist to localStorage with this key
    }
  )
);

// Store selector hook for user gifts (doesn't trigger fetches)
export function useUserGiftsFromStore() {
  const { userGifts, isLoadingGifts, giftsError } = useUserStore();
  
  return {
    gifts: userGifts,
    isLoading: isLoadingGifts,
    error: giftsError,
    totalGifts: userGifts.length,
    distributedGifts: userGifts.filter(gift => gift.giftStatus === 'DISTRIBUTED').length,
    generatedGifts: userGifts.filter(gift => gift.giftStatus === 'GENERATED').length,
    pendingGifts: userGifts.filter(gift => gift.giftStatus === 'PENDING').length,
  };
}